import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import { Box, Button, Container, IconButton } from '@mui/material';
import Page from '../../components/Page';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  setLimit,
  setSkip,
  setCurrentPage,
  setStatus,
} from '../../store/country/countrySlice';
import {
  findCountry,
  createCountry,
  patchCountry,
} from '../../store/country/api';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import useToggle from '../../hooks/useToggle';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Scrollbar from '../../components/Scrollbar';
import Searchbar from '../../components/Searchbar';
import { omit } from 'lodash';
import {
  reset as resetCountry,
  setStatus as setCountryStatus,
} from '../../store/country/countrySlice';
import {
  reset as resetSelectCountry,
  setStatus as setSelectCountryStatus,
} from '../../store/selectCountry/selectCountrySlice';
import countrySchema from '../../validation/countrySchema';
import CountryFormModal from './CountryFormModal';
import { Country } from '../../store/country/api/interface';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  // const { t } = useTranslation();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.country);
  // const organizationId = getItem(config.ORGANIZATION_ID);
  const [search, setSearch] = useState<string>('');
  const { toggle, onOpen, onClose } = useToggle();

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findCountry({
          limit,
          skip,
          ...(search && { search: search }),
        })
      );
    }
  }, [dispatch, limit, skip, status]);

  useEffect(() => {
    dispatch(resetCountry());
  }, [search]);

  const {
    handleSubmit,
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    setValues,
  } = useFormik({
    initialValues: {
      id: '',
      labelEn: '',
      labelAr: '',
      emoji: '',
      code: '',
      activeState: true,
    },
    validationSchema: countrySchema,
    onSubmit: async (values) => {
      try {
        if (values.id) {
          await dispatch(patchCountry(values)).unwrap();
        }
        if (!values.id) {
          const reqBody = omit(values, ['id']);
          await dispatch(createCountry(reqBody)).unwrap();
        }
        onClose();
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
        dispatch(resetSelectCountry());
        dispatch(setSelectCountryStatus({ status: 'idle' }));
      } catch (error: any) {}
    },
  });

  const handleUpdate = (value: Country): void => {
    onOpen();
    setValues({
      id: value.id,
      labelEn: value?.labelEn,
      labelAr: value?.labelAr,
      emoji: value?.emoji,
      code: value?.code,
      activeState: value?.activeState,
    });
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setCountryStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  return (
    <>
      <Page title={'Country: List'}>
        <Container maxWidth={'xl'}>
          <HeaderBreadcrumbs
            heading={'Country'}
            links={[{ name: 'Dashboard', href: '/' }]}
            action={
              <Button
                variant='contained'
                startIcon={<Iconify icon={'eva:plus-fill'} />}
                onClick={onOpen}
              >
                {'New Country'}
              </Button>
            }
          />

          <Card>
            <Searchbar handleSearch={handleSearch} />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Label EN'}</TableCell>
                      <TableCell align='center'>{'Label AR'}</TableCell>
                      <TableCell align='center'>{'Emoji'}</TableCell>
                      <TableCell align='center'>{'Code'}</TableCell>
                      <TableCell align='center'>{'Status'}</TableCell>
                      <TableCell align='center'>{'Action'}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : data?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.map((country) => (
                        <TableRow key={country.id}>
                          <TableCell align='center'>
                            {country?.labelEn}
                          </TableCell>
                          <TableCell align='center'>
                            {country?.labelAr}
                          </TableCell>
                          <TableCell align='center'>
                            {' '}
                            <span
                              dangerouslySetInnerHTML={{
                                __html: country.emoji,
                              }}
                            />
                          </TableCell>
                          <TableCell align='center'>{country?.code}</TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={country.activeState ? 'success' : 'error'}
                            >
                              {country.activeState ? 'Active' : 'InActive'}
                            </Label>
                          </TableCell>
                          <TableCell align='center'>
                            <IconButton
                              aria-label='edit'
                              onClick={() => handleUpdate(country)}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component='div'
                count={totalRecords}
                rowsPerPage={limit}
                page={Math.min(Math.round(totalRecords / limit), currentPage)}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Card>
          <CountryFormModal
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onClose={onClose}
            resetForm={resetForm}
            setFieldValue={setFieldValue}
            toggle={toggle}
            values={values}
          />
        </Container>
      </Page>
    </>
  );
};

export default Index;
